import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Button from "@mui/material/Button";
import { useAppState } from "../../hooks/useAppState";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { showToast } from "../../../toast";
import {
  setGameSocketHandlers,
  onSocketOpen,
  type GameAnsweredData,
  type GameCardDrawnData,
  type GameDeclinedData,
  type GameEndedData,
  type GameFinishedData,
  type GameInviteData,
  type GameJoinedData,
  type GameSkippedData,
} from "../../../chatSocket";
import {
  acceptGameApi,
  answerCardApi,
  applyGameAnswered,
  applyGameCardDrawn,
  applyGameDeclined,
  applyGameEnded,
  applyGameFinished,
  applyGameInvite,
  applyGameJoined,
  applyGameSkipped,
  declineGameApi,
  drawCardApi,
  fetchGameDecksApi,
  fetchGameSessionApi,
  inviteGameApi,
  leaveGameApi,
  skipCardApi,
} from "../../../gameApi";
import { GameHomeDialog } from "./GameHomeDialog";
import { GameStage } from "./GameStage";
import type { GameDecksResponse } from "../../../types";

// Mounted once in AppRoot, always present, self-hiding via appState - same
// convention as TogetherRoot.tsx, which this file mirrors closely (session
// shape, socket handler registration, portal-outside-#react-root reasoning
// below). Two things Together Mode needed that this feature deliberately
// skips (see the design plan): no echo-guard (game state only ever changes
// via an explicit REST call whose result pushes to the other user - there's
// no local-UI-driven echo source) and no host-join-sync push (game state is
// fully server-authored at every step; the accept/resync response already
// carries a fresh, correct session).
export function GameRoot() {
  const gameSession = useAppState((s) => s.gameSession);
  const gameIncomingInvite = useAppState((s) => s.gameIncomingInvite);
  const gameDialogOpen = useAppState((s) => s.gameDialogOpen);

  const [respondingToInvite, setRespondingToInvite] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [skipping, setSkipping] = useState(false);
  // What to show inline once a card's been answered - the actual answer
  // TEXT isn't part of GameSession (that only tracks resolution state), so
  // this is transient local UI state, set from whichever side learns of it
  // first (a local optimistic submit, or the game:answered push).
  const [lastAnswer, setLastAnswer] = useState<{ text: string; senderName: string; mine: boolean } | null>(null);
  // Static reference data (label/emoji/description per deck + intensity
  // levels) for the invite banner and GameStage's header chip - fetched
  // once (fetchGameDecksApi caches it), not per-render.
  const [decksMeta, setDecksMeta] = useState<GameDecksResponse | null>(null);

  useEffect(() => {
    fetchGameDecksApi().then(setDecksMeta);
  }, []);

  // If the very first attempt above lost to a transient network hiccup,
  // decksMeta stays null and GameHomeDialog is stuck on "Loading decks…"
  // for the rest of the session - nothing previously ever tried again.
  // fetchGameDecksApi() now reliably clears its own cache on any failure
  // (see gameApi.ts), so retrying here whenever the dialog is (re)opened
  // with decksMeta still missing is a real retry, not a no-op.
  useEffect(() => {
    if (gameDialogOpen && !decksMeta) {
      fetchGameDecksApi().then(setDecksMeta);
    }
  }, [gameDialogOpen, decksMeta]);

  useEffect(() => {
    setGameSocketHandlers({
      onGameInvite: (data: GameInviteData) => applyGameInvite(data),
      onGameJoined: (data: GameJoinedData) => applyGameJoined(data),
      onGameDeclined: (data: GameDeclinedData) => applyGameDeclined(data),
      onGameCardDrawn: (data: GameCardDrawnData) => {
        applyGameCardDrawn(data);
        setLastAnswer(null);
      },
      onGameAnswered: (data: GameAnsweredData) => {
        applyGameAnswered(data);
        setLastAnswer({ text: data.text, senderName: data.senderName, mine: false });
      },
      onGameSkipped: (data: GameSkippedData) => {
        applyGameSkipped(data);
        setLastAnswer(null);
      },
      onGameFinished: (data: GameFinishedData) => applyGameFinished(data),
      onGameEnded: (data: GameEndedData) => {
        applyGameEnded(data);
        setLastAnswer(null);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // game:* events are push-only over the WS - anything that happened while
  // this socket was down is otherwise silently missed, not just delayed.
  // Same substrate/reasoning as togetherSync.ts's resyncAfterReconnect, but
  // inlined here rather than a separate file: unlike Together Mode there's
  // no <video> element or echo-guard to coordinate, so a plain fetch-and-
  // replace on reconnect is the whole story.
  //
  // Deliberately unconditional (no "only if we already have local state"
  // guard) - onSocketOpen fires on the very FIRST connect too, and
  // appState.gameSession/gameIncomingInvite both start null every page
  // load. Without this running on that first connect, a page refresh
  // mid-game would show nothing until some other action happened to
  // re-sync it - the exact bug this effect exists to prevent.
  useEffect(() => {
    return onSocketOpen(() => {
      const hadSession = !!appState.gameSession;
      const hadInvite = !!appState.gameIncomingInvite;
      fetchGameSessionApi().then((fresh) => {
        if (!fresh) {
          if (hadSession) showToast("Your game ended while you were disconnected.", "info");
          if (hadSession || hadInvite) {
            appState.gameSession = null;
            appState.gameIncomingInvite = null;
            notifyStateChange();
          }
          return;
        }

        if (fresh.status === "pending" && !fresh.isHost) {
          // Recipient hasn't accepted yet - gameByUser already includes
          // them for a pending invite (mirrors Together Mode's
          // togetherByUser), so this branch is reachable. They should see
          // the accept/decline banner, not GameStage's "waiting" view.
          appState.gameSession = null;
          appState.gameIncomingInvite = {
            sessionId: fresh.id,
            fromUserId: fresh.peerUserId,
            fromName: fresh.peerName,
            mode: fresh.mode,
            intensity: fresh.intensity,
            rematch: false,
            conversationId: fresh.conversationId,
          };
        } else {
          appState.gameSession = fresh;
          appState.gameIncomingInvite = null;
        }
        notifyStateChange();
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!gameSession) setLastAnswer(null);
  }, [gameSession?.id]);

  async function handleAccept(): Promise<void> {
    if (!gameIncomingInvite) return;
    setRespondingToInvite(true);
    const result = await acceptGameApi(gameIncomingInvite.sessionId);
    setRespondingToInvite(false);
    if (!result.ok) showToast(result.error || "Failed to join game.", "error");
  }

  async function handleDecline(): Promise<void> {
    if (!gameIncomingInvite) return;
    setRespondingToInvite(true);
    await declineGameApi(gameIncomingInvite.sessionId);
    setRespondingToInvite(false);
  }

  async function handleLeave(): Promise<void> {
    if (!gameSession) return;
    setLeaving(true);
    await leaveGameApi(gameSession.id);
    setLeaving(false);
  }

  async function handleDraw(): Promise<void> {
    if (!gameSession) return;
    setDrawing(true);
    setLastAnswer(null);
    const result = await drawCardApi(gameSession.id);
    setDrawing(false);
    if (!result.ok) showToast(result.error || "Failed to draw a card.", "error");
  }

  async function handleSkip(): Promise<void> {
    if (!gameSession) return;
    setSkipping(true);
    const result = await skipCardApi(gameSession.id);
    setSkipping(false);
    if (!result.ok) showToast(result.error || "Failed to skip.", "error");
  }

  function closeInviteDialog(): void {
    appState.gameDialogOpen = false;
    notifyStateChange();
  }

  async function handleAnswer(text: string): Promise<void> {
    if (!gameSession) return;
    const result = await answerCardApi(gameSession.id, text);
    if (result.ok) {
      setLastAnswer({ text, senderName: "You", mine: true });
    } else {
      showToast(result.error || "Failed to send answer.", "error");
    }
  }

  // Fresh session, same deck/intensity, through the full existing invite/
  // accept handshake again - not a rematch-shortcut endpoint, per the
  // instruction to extend existing systems rather than add parallel ones.
  async function handlePlayAgain(): Promise<void> {
    if (!gameSession) return;
    const { mode, intensity } = gameSession;
    await leaveGameApi(gameSession.id);
    const result = await inviteGameApi({ mode, intensity: intensity ?? undefined, rematch: true });
    if (!result.ok) showToast(result.error || "Failed to start a new game.", "error");
  }

  async function handleChooseAnotherDeck(): Promise<void> {
    if (gameSession) await leaveGameApi(gameSession.id);
    appState.gameDialogOpen = true;
    notifyStateChange();
  }

  function deckMetaFor(deckId: string) {
    return decksMeta?.decks.find((d) => d.id === deckId) ?? null;
  }
  function intensityMetaFor(level: number | null) {
    return level == null ? null : (decksMeta?.intensityLevels.find((l) => l.level === level) ?? null);
  }

  // Portaled to document.body, outside #react-root - same reasoning as
  // TogetherRoot.tsx: MUI's ModalManager marks every OTHER child of
  // #react-root aria-hidden while any Dialog (e.g. the Lightbox, or this
  // feature's own GameHomeDialog) is open, which would silently make a
  // plain-sibling overlay's controls unfocusable/untappable.
  return createPortal(
    <>
      <GameHomeDialog open={gameDialogOpen && !gameSession} onClose={closeInviteDialog} decksMeta={decksMeta} />

      {gameIncomingInvite &&
        (() => {
          const deck = gameIncomingInvite.mode === "mixed" ? null : deckMetaFor(gameIncomingInvite.mode);
          const intensity = intensityMetaFor(gameIncomingInvite.intensity);
          const deckLabel = gameIncomingInvite.mode === "mixed" ? "Mixed" : (deck?.label ?? gameIncomingInvite.mode);
          const deckEmoji = gameIncomingInvite.mode === "mixed" ? "\u{1F3B4}" : (deck?.emoji ?? "\u{1F3AE}");
          return (
            <div
              className="glass p-3"
              style={{
                position: "fixed",
                bottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1400,
                minWidth: 280,
                maxWidth: "90vw",
              }}
            >
              <div className="d-flex align-items-center gap-2 mb-2">
                <span style={{ fontSize: "1.3rem" }}>{deckEmoji}</span>
                <span className="fw-semibold">
                  {gameIncomingInvite.fromName} wants to play{gameIncomingInvite.rematch ? " again" : ""} {deckLabel}
                  {intensity ? ` (${intensity.label})` : ""}
                </span>
              </div>
              <div className="d-flex gap-2 justify-content-end">
                <Button size="small" onClick={handleDecline} disabled={respondingToInvite}>
                  Decline
                </Button>
                <Button size="small" variant="contained" color="primary" onClick={handleAccept} disabled={respondingToInvite}>
                  Play
                </Button>
              </div>
            </div>
          );
        })()}

      {gameSession && (
        <GameStage
          session={gameSession}
          deckMeta={gameSession.mode === "mixed" ? null : deckMetaFor(gameSession.mode)}
          intensityMeta={intensityMetaFor(gameSession.intensity)}
          lastAnswer={lastAnswer}
          leaving={leaving}
          drawing={drawing}
          skipping={skipping}
          onDraw={handleDraw}
          onSkip={handleSkip}
          onAnswer={handleAnswer}
          onLeave={handleLeave}
          onPlayAgain={handlePlayAgain}
          onChooseAnotherDeck={handleChooseAnotherDeck}
        />
      )}
    </>,
    document.body,
  );
}
